// src/components/ui/card.js
import React from 'react';
import { cn } from '../../lib/utils';

const Card = ({ 
  children, 
  variant = 'default', 
  className = '', 
  style = {},
  ...props 
}) => {
  const baseClasses = 'rounded-xl shadow-lg';
  
  const variants = {
    // Default glassmorphism (most common in your codebase)
    default: 'bg-white/10 backdrop-blur-md border border-white/10',
    
    // Light glassmorphism (used in SiteInformationCard)
    light: 'bg-white/70 backdrop-blur-md border border-gray-200',
    
    // Dark glassmorphism (used in LiveRideCard)
    dark: 'bg-gray-800/60 backdrop-blur-md border border-white/20',
    
    // Advanced gradient (used in CurrentStatsCard and HomePage)
    gradient: 'bg-gradient-to-br from-white/70 to-blue-50/50 backdrop-blur-md border border-white',
    
    // Transparent with backdrop blur
    transparent: 'bg-transparent backdrop-blur-md border border-white/20',
    
    // Solid white
    solid: 'bg-white border border-gray-200',
    
    // Custom - allows passing custom classes
    custom: ''
  };

  const variantClasses = variants[variant] || variants.default;
  
  return (
    <div 
      className={cn(baseClasses, variantClasses, className)}
      style={style}
      {...props}
    >
      {children}
    </div>
  );
};

// Card variants with predefined styles for common use cases
Card.Glassmorphism = ({ children, className, ...props }) => (
  <Card variant="default" className={className} {...props}>
    {children}
  </Card>
);

Card.Light = ({ children, className, ...props }) => (
  <Card variant="light" className={className} {...props}>
    {children}
  </Card>
);

Card.Dark = ({ children, className, ...props }) => (
  <Card variant="dark" className={className} {...props}>
    {children}
  </Card>
);

Card.Gradient = ({ children, className, style, ...props }) => (
  <Card 
    variant="gradient" 
    className={className}
    style={{
      background: 'radial-gradient(184.58% 152.09% at 136.24% -51.3%, rgba(10, 128, 137, 0.09) 0%, rgba(255, 255, 255, 0.72) 100%)',
      boxShadow: '0px 15px 30px 0px rgba(0,0,0,0.10), 0px 5px 20.100000381469727px_0px_rgba(255,255,255,0.35) inset',
      outline: '1px solid white',
      backdropFilter: 'blur(30px)',
      ...style
    }}
    {...props}
  >
    {children}
  </Card>
);

Card.NoShadowGradient = ({ children, className, style, ...props }) => (
  <Card 
    variant="gradient" 
    className={cn('shadow-none', className)}
    style={{
      background: 'radial-gradient(184.58% 152.09% at 136.24% -51.3%, rgba(10, 128, 137, 0.09) 0%, rgba(255, 255, 255, 0.72) 100%)',
      outline: '1px solid white',
      backdropFilter: 'blur(30px)',
      ...style
    }}
    {...props}
  >
    {children}
  </Card>
);

export default Card;
